import { USER_ROLE } from "Constants/constant";
import { apiGetRequest } from "helpers/Requests";
import { roleAccess } from "helpers/RolesPermission";
import { apiCall } from "./service";
import axios from "./axios";
import {
  URL_INCIDENT_CLOSE,
  URL_INCIDENT_CLOSE_DRAFT,
  URL_INCIDENT_CREATE,
  URL_INCIDENT_DASHBOARD,
  URL_INCIDENT_DELETE,
  URL_INCIDENT_DETAIL,
  URL_INCIDENT_EXPORT,
  URL_INCIDENT_INVESTIGATE,
  URL_INCIDENT_LIST,
  URL_INCIDENT_PDF,
  URL_INCIDENT_UPDATE,
  URL_INCIDENT_WORKER_CREATE,
  URL_INCIDENT_WORKER_DASHBOARD,
  URL_INCIDENT_WORKER_DELETE,
  URL_INCIDENT_WORKER_DETAIL,
  URL_INCIDENT_WORKER_LIST,
  URL_INCIDENT_WORKER_PDF,
  URL_INCIDENT_WORKER_TIMESHEETS,
  URL_INCIDENT_WORKER_UPDATE,
  URL_INCIDENT_TIMESHEETS,
  URL_SP_PARTICIPANTS,
} from "./URL";

const api = {
  // Get incident list
  getIncidentList(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_LIST
      : URL_INCIDENT_WORKER_LIST;
    return apiCall.post({
      url,
      isAuthToken: true,
      params: params.filters || {},
      bodyParams: params.bodyFilter || null,
    });
  },

  // Get dashboard stats
  getIncidentDashboard(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_DASHBOARD
      : URL_INCIDENT_WORKER_DASHBOARD;
    return apiCall.get({
      url,
      isAuthToken: true,
      params: params || {},
    });
  },

  // Get single incident detail
  getIncidentDetail(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_DETAIL
      : URL_INCIDENT_WORKER_DETAIL;
    return apiCall.get({
      url: `${url}/${params.uuid}`,
      isAuthToken: true,
      params: {},
    });
  },

  // Generate incident report PDF and return full response (blob) for download.
  // Timeout so the UI never stays stuck on "Generating…" if the server hangs.
  getIncidentPdfBlob(params) {
    const baseUrl = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_PDF
      : URL_INCIDENT_WORKER_PDF;
    const url = `${baseUrl}/${params.uuid}/pdf`;
    const token = localStorage.getItem("accessToken");
    let spId;
    try {
      const cached = localStorage.getItem("selectedProvider");
      if (cached) {
        const parsed = JSON.parse(cached);
        spId = parsed?.value;
      }
    } catch (_) {}
    const headers = {
      Accept: "application/pdf",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(spId && { sp_id: spId }),
    };
    return axios.get(url, {
      responseType: "blob",
      headers,
      timeout: 90000,
    });
  },

  // Create new incident
  createIncident(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_CREATE
      : URL_INCIDENT_WORKER_CREATE;
    return apiCall.post({
      url,
      isAuthToken: true,
      params,
    });
  },

  // Update incident (worker)
  updateIncident(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_UPDATE
      : URL_INCIDENT_WORKER_UPDATE;
    return apiCall.put({
      url: `${url}/${params.uuid}`,
      isAuthToken: true,
      params,
    });
  },

  // Start investigation (manager)
  investigateIncident(params) {
    return apiCall.put({
      url: `${URL_INCIDENT_INVESTIGATE}/${params.uuid}`,
      isAuthToken: true,
      params,
    });
  },

  // Close/report incident (manager)
  closeIncident(params) {
    return apiCall.put({
      url: `${URL_INCIDENT_CLOSE}/${params.uuid}`,
      isAuthToken: true,
      params,
    });
  },

  // Save close review draft (manager)
  closeIncidentDraft(params) {
    return apiCall.put({
      url: `${URL_INCIDENT_CLOSE_DRAFT}/${params.uuid}`,
      isAuthToken: true,
      params,
    });
  },

  // Delete incident (soft delete)
  deleteIncident(params) {
    const url = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_DELETE
      : URL_INCIDENT_WORKER_DELETE;
    return apiCall.delete({
      url: `${url}/${params.uuid}`,
      isAuthToken: true,
      params: {}, // Required by apiCall.delete
    });
  },

  // Export incidents
  exportIncidents(params) {
    return apiCall.post({
      url: URL_INCIDENT_EXPORT,
      isAuthToken: true,
      params,
    });
  },

  // Get assigned participants (for Worker Portal)
  getAssignedParticipants() {
    return apiGetRequest("/hrm/feedback/participants");
  },

  // Get all participants (for SP Admin/Manager)
  getAllParticipants(params) {
    return apiCall.post({
      url: URL_SP_PARTICIPANTS,
      isAuthToken: true,
      params: params?.filters || { page: 1, per_page: 500 },
      bodyFilter: params?.bodyFilter || null,
    });
  },

  bulkGeneratePdf(params) {
    // SP admin/manager vs worker use different base URLs
    const baseUrl = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_LIST.replace('/incidents', '')
      : URL_INCIDENT_WORKER_LIST.replace('/incidents', '');
    return apiCall.post({
      url: `${baseUrl}/incidents/bulk-pdf`,
      isAuthToken: true,
      params: {},
      bodyParams: { uuids: params.uuids || [] },
    });
  },

  listTimesheets(workerIds) {
    const ids = (workerIds || []).filter((id) => Number(id) > 0);
    const base = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_TIMESHEETS
      : URL_INCIDENT_WORKER_TIMESHEETS;
    const query = ids
      .map((id) => `worker_ids[]=${encodeURIComponent(id)}`)
      .join("&");
    return apiCall.get({
      url: query ? `${base}?${query}` : base,
      isAuthToken: true,
      params: {},
    });
  },

  timesheetParticipants(uuid) {
    const base = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager])
      ? URL_INCIDENT_TIMESHEETS
      : URL_INCIDENT_WORKER_TIMESHEETS;
    return apiCall.get({
      url: `${base}/${uuid}/participants`,
      isAuthToken: true,
      params: {},
    });
  },
};

export default api;
